import {
  type PointerEvent as ReactPointerEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react"

import { FleetB2BPageShell } from "@/features/operator/fleet/fleet-b2b-page-shell"
import { applyProvisionedFleetDraft } from "@/features/operator/fleet/fleet-onboarding-run-hydration"
import {
  applyFleetOnboardingRouteContext,
  createFleetManualAssetDraft,
  FLEET_ONBOARDING_MODE_LABELS,
  type FleetOnboardingSearch,
  resolveFleetOnboardingMode,
  resolveFleetOnboardingVertical,
  FLEET_VERTICAL_ASSET_LABELS,
  FLEET_VERTICAL_LABELS,
  FLEET_VERTICAL_NOTES_LABELS,
  FLEET_VERTICAL_PRIMARY_OPERATOR_LABELS,
  FLEET_VERTICAL_ROUTE_LABELS,
  FLEET_VERTICAL_SECONDARY_OPERATOR_LABELS,
  summarizeFleetOnboardingDraft,
  useFleetOnboardingDraft,
} from "@/features/operator/fleet/fleet-onboarding-shared"
import { FleetOnboardingStepper } from "@/features/operator/fleet/fleet-onboarding-stepper"
import { getOperatorSessionOrganization } from "@/features/operator/shared/operator-access"
import {
  OperatorEmptyState,
  OperatorMetricCard,
  OperatorPanel,
  OperatorStatusMessage,
} from "@/features/operator/shared/operator-shell"
import { useOptionalOperatorShellSession } from "@/features/operator/shared/operator-shell-session-context"
import { apiClient, isApiError } from "@/lib/api/client"
import { cn } from "@/lib/utils"

interface FleetOnboardingManualPageProps {
  onContinueToReview: () => void
  search: FleetOnboardingSearch
}

type FleetManualAssetField =
  | "assetNumber"
  | "notes"
  | "operatingUnitId"
  | "primaryOperatorName"
  | "primaryOperatorPhone"
  | "routeOrZone"
  | "secondaryOperatorName"
  | "secondaryOperatorPhone"

export function FleetOnboardingManualPage({
  onContinueToReview,
  search,
}: FleetOnboardingManualPageProps) {
  const shellSession = useOptionalOperatorShellSession()
  const { draft, setDraft } = useFleetOnboardingDraft()
  const draftRef = useRef(draft)
  draftRef.current = draft
  const summary = summarizeFleetOnboardingDraft(draft)
  const [generationError, setGenerationError] = useState<string | null>(null)
  const [hydrationError, setHydrationError] = useState<string | null>(null)
  const [isHydratingRun, setIsHydratingRun] = useState(false)
  const hydratedContextRef = useRef<string | null>(null)
  const manualAssetCountInputRef = useRef<HTMLInputElement | null>(null)
  const generatedOnPointerDownAtRef = useRef<number | null>(null)

  useEffect(() => {
    setDraft((previous) =>
      applyFleetOnboardingRouteContext(previous, {
        mode: search.mode,
        organizationId: search.organizationId,
        runId: search.runId,
        vertical: search.vertical,
      })
    )
  }, [search.mode, search.organizationId, search.runId, search.vertical, setDraft])

  const selectedMode = resolveFleetOnboardingMode(search.mode, draft, search.step)
  const selectedVertical = resolveFleetOnboardingVertical(search.vertical, draft)
  const sessionOrganizationId = useMemo(() => {
    if (shellSession?.status !== "ready") {
      return null
    }

    return getOperatorSessionOrganization(shellSession.session).id
  }, [shellSession])
  const assetLabel = FLEET_VERTICAL_ASSET_LABELS[selectedVertical]
  const primaryOperatorLabel = FLEET_VERTICAL_PRIMARY_OPERATOR_LABELS[selectedVertical]
  const secondaryOperatorLabel = FLEET_VERTICAL_SECONDARY_OPERATOR_LABELS[selectedVertical]
  const routeLabel = FLEET_VERTICAL_ROUTE_LABELS[selectedVertical]
  const notesLabel = FLEET_VERTICAL_NOTES_LABELS[selectedVertical]

  const completeOperatingUnits = draft.units.filter((unit) => {
    return unit.code.trim() && unit.name.trim() && unit.city.trim()
  })

  const readinessSummary = useMemo(() => {
    const completeAssets = draft.manualAssets.filter((asset) => {
      return asset.assetNumber.trim() && asset.operatingUnitId.trim()
    }).length

    const assetsMissingNumber = draft.manualAssets.filter((asset) => !asset.assetNumber.trim()).length
    const mappedUnits = new Set(
      draft.manualAssets
        .map((asset) => asset.operatingUnitId.trim())
        .filter(Boolean)
    ).size

    return {
      assetsMissingNumber,
      completeAssets,
      mappedUnits,
    }
  }, [draft.manualAssets])

  useEffect(() => {
    let isActive = true
    const hydrateRun = async () => {
      const targetOrganizationId =
        search.organizationId || draft.activeOrganizationId || sessionOrganizationId || undefined
      const targetRunId = search.runId || draft.activeRunId || undefined

      if (!targetOrganizationId) {
        if (!isActive) {
          return
        }

        setHydrationError(null)
        setIsHydratingRun(false)
        return
      }

      const contextKey = `${targetOrganizationId}:${targetRunId || "active"}`
      if (hydratedContextRef.current === contextKey) {
        if (!isActive) {
          return
        }

        setHydrationError(null)
        setIsHydratingRun(false)
        return
      }

      setHydrationError(null)
      setIsHydratingRun(true)

      try {
        const resolved = await apiClient.resolveFleetOnboardingRun({
          onboardingMode: selectedMode,
          organizationId: targetOrganizationId,
          runId: targetRunId,
          step: "manual",
          vertical: selectedVertical,
        })
        if (!isActive) {
          return
        }

        hydratedContextRef.current = contextKey
        setGenerationError(null)
        setDraft((previous) => {
          const withRouteContext = applyFleetOnboardingRouteContext(previous, {
            mode: resolved.run.onboardingMode || selectedMode,
            organizationId: resolved.run.organizationId,
            runId: resolved.run.id,
            vertical: resolved.run.vertical || selectedVertical,
          })
          const ownerProfile =
            shellSession?.status === "ready" && shellSession.session.actorType === "user"
              ? shellSession.session.profile
              : null

          return applyProvisionedFleetDraft(
            withRouteContext,
            resolved.organization,
            ownerProfile
          )
        })
      } catch (error) {
        if (!isActive) {
          return
        }

        setHydrationError(
          isApiError(error)
            ? error.message
            : error instanceof Error
              ? error.message
              : "Failed to load onboarding organization details for manual entry."
        )
      } finally {
        if (isActive) {
          setIsHydratingRun(false)
        }
      }
    }

    void hydrateRun()

    return () => {
      isActive = false
    }
  }, [
    draft.activeOrganizationId,
    draft.activeRunId,
    search.organizationId,
    search.runId,
    selectedMode,
    selectedVertical,
    sessionOrganizationId,
    setDraft,
    shellSession,
  ])

  function updateDraftField(key: "manualAssetCount", value: string) {
    setDraft((previous) => ({
      ...previous,
      kitIssuance: null,
      masterPersistence: null,
      [key]: value,
    }))
  }

  function updateManualAsset(
    assetId: string,
    key: FleetManualAssetField,
    value: string
  ) {
    setDraft((previous) => ({
      ...previous,
      kitIssuance: null,
      manualAssets: previous.manualAssets.map((asset) =>
        asset.id === assetId
          ? {
              ...asset,
              [key]: key === "assetNumber" ? value.toUpperCase() : value,
            }
          : asset
      ),
      masterPersistence: null,
    }))
  }

  function generateManualRows() {
    const liveCountValue =
      manualAssetCountInputRef.current?.value.replace(/[^\d]/g, "")
      || draftRef.current.manualAssetCount
    const currentDraft = draftRef.current
    const nextCount = Number.parseInt(liveCountValue, 10)
    if (!Number.isFinite(nextCount) || nextCount <= 0) {
      setGenerationError(`Enter how many ${assetLabel.toLowerCase()}s this onboarding batch needs.`)
      return
    }

    const nextCompleteOperatingUnits = currentDraft.units.filter((unit) => {
      return unit.code.trim() && unit.name.trim() && unit.city.trim()
    })

    if (nextCompleteOperatingUnits.length === 0) {
      setGenerationError("Finish at least one operating unit with code, name, and city before generating asset rows.")
      return
    }

    setGenerationError(null)

    setDraft((previous) => ({
      ...previous,
      kitIssuance: null,
      manualAssetCount: liveCountValue,
      manualAssets: Array.from({ length: nextCount }, (_, index) =>
        createFleetManualAssetDraft({
          operatingUnitId:
            previous.manualAssets[index]?.operatingUnitId
            || nextCompleteOperatingUnits.find((unit) => unit.isPrimary)?.id
            || nextCompleteOperatingUnits[0]?.id
            || "",
          primaryOperatorName: previous.manualAssets[index]?.primaryOperatorName || "",
          primaryOperatorPhone: previous.manualAssets[index]?.primaryOperatorPhone || "",
          routeOrZone: previous.manualAssets[index]?.routeOrZone || "",
          secondaryOperatorName: previous.manualAssets[index]?.secondaryOperatorName || "",
          secondaryOperatorPhone: previous.manualAssets[index]?.secondaryOperatorPhone || "",
          notes: previous.manualAssets[index]?.notes || "",
          assetNumber: previous.manualAssets[index]?.assetNumber || "",
        })
      ),
      masterPersistence: null,
    }))
  }

  function handleGeneratePointerDown(event: ReactPointerEvent<HTMLButtonElement>) {
    if (
      event.pointerType === "mouse"
      && event.button !== 0
    ) {
      return
    }

    generatedOnPointerDownAtRef.current = Date.now()
    generateManualRows()
  }

  function handleGenerateClick() {
    if (
      generatedOnPointerDownAtRef.current !== null
      && Date.now() - generatedOnPointerDownAtRef.current < 1000
    ) {
      generatedOnPointerDownAtRef.current = null
      return
    }

    generatedOnPointerDownAtRef.current = null
    generateManualRows()
  }

  function clearManualRows() {
    setGenerationError(null)
    setDraft((previous) => ({
      ...previous,
      kitIssuance: null,
      manualAssets: [],
      manualAssetCount: "",
      masterPersistence: null,
    }))
  }

  const canContinueToReview =
    draft.manualAssets.length > 0 && readinessSummary.completeAssets === draft.manualAssets.length

  return (
    <FleetB2BPageShell
      activeSection="onboarding"
      title="Manual asset onboarding"
      description="Support smaller fleets and pilots by letting operators create asset, crew, and assignment drafts directly in Fleet before kit issuance."
      highlights={[
        `Generate structured ${assetLabel.toLowerCase()} rows from an operator-supplied asset count.`,
        `Require every ${assetLabel.toLowerCase()} to have an asset number and operating-unit mapping before review.`,
        "Capture primary and secondary operator details so later phases can turn drafts into real people and assignment records.",
      ]}
    >
      <div className="grid gap-5">
        <OperatorPanel
          title="Onboarding progress"
          description="Phase 4 activates the manual roster flow for smaller fleets, pilots, and branch-by-branch rollouts."
        >
          <FleetOnboardingStepper activeStep="manual" />
        </OperatorPanel>

        <OperatorStatusMessage>
          Vertical: <strong>{FLEET_VERTICAL_LABELS[selectedVertical]}</strong>. Next working surface:
          <strong> {FLEET_ONBOARDING_MODE_LABELS.manual}</strong>.
        </OperatorStatusMessage>

        <section className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_minmax(0,0.95fr)]">
          <OperatorPanel
            title="Manual roster generator"
            description={`Generate one structured ${assetLabel.toLowerCase()} row per asset. This is the B2B path for smaller fleets that do not need workbook upload.`}
          >
            <div className="grid gap-4">
              <label className="grid gap-2 text-sm md:max-w-xs">
                <span className="text-muted-foreground">{assetLabel} count</span>
                <input
                  className="rounded-2xl border border-border bg-background px-3 py-2"
                  inputMode="numeric"
                  ref={manualAssetCountInputRef}
                  value={draft.manualAssetCount}
                  onChange={(event) =>
                    updateDraftField("manualAssetCount", event.target.value.replace(/[^\d]/g, ""))
                  }
                  placeholder="10"
                />
              </label>

              <div className="flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={handleGenerateClick}
                  onPointerDown={handleGeneratePointerDown}
                  disabled={isHydratingRun}
                  className="rounded-2xl bg-primary px-4 py-2 text-sm font-medium text-primary-foreground disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Generate draft rows
                </button>
                <button
                  type="button"
                  onClick={clearManualRows}
                  className="rounded-2xl border border-border px-4 py-2 text-sm font-medium"
                >
                  Clear manual roster
                </button>
              </div>

              {isHydratingRun ? (
                <OperatorStatusMessage>
                  Loading onboarding organization details for this manual run.
                </OperatorStatusMessage>
              ) : null}

              {hydrationError ? (
                <OperatorStatusMessage tone="danger">{hydrationError}</OperatorStatusMessage>
              ) : null}

              {generationError ? (
                <OperatorStatusMessage tone="warning">{generationError}</OperatorStatusMessage>
              ) : null}

              <div className="rounded-3xl border border-border/70 bg-background/65 px-4 py-4 text-sm leading-6 text-muted-foreground">
                Draft organization: <strong>{draft.organizationName.trim() || "Not captured yet"}</strong>
                <br />
                Complete operating units: <strong>{summary.completeUnits}</strong> / <strong>{summary.totalUnits}</strong>
                <br />
                Driver language default: <strong>{draft.preferredDriverLanguage}</strong>
              </div>
            </div>
          </OperatorPanel>

          <OperatorPanel
            title="Manual readiness"
            description="Every row needs an asset number and operating-unit mapping before review can continue."
          >
            <div className="grid gap-3 md:grid-cols-2">
              <OperatorMetricCard
                label={`${assetLabel} rows`}
                value={draft.manualAssets.length}
                detail="Generated rows currently held in the onboarding draft."
              />
              <OperatorMetricCard
                label="Review-ready rows"
                value={readinessSummary.completeAssets}
                detail="Rows with both required asset number and operating-unit assignment."
              />
              <OperatorMetricCard
                label="Missing asset numbers"
                value={readinessSummary.assetsMissingNumber}
                detail={`Rows that still need the required ${assetLabel.toLowerCase()} number.`}
              />
              <OperatorMetricCard
                label="Mapped units"
                value={readinessSummary.mappedUnits}
                detail="Distinct branches, depots, hubs, or route bases referenced by the current roster."
              />
            </div>
          </OperatorPanel>
        </section>

        <OperatorPanel
          title={`${assetLabel} roster`}
          description={`This is the structured manual draft table for ${assetLabel.toLowerCase()} onboarding. Later phases will convert these rows into real asset, people, and assignment records.`}
        >
          {draft.manualAssets.length === 0 ? (
            <OperatorEmptyState
              title="No manual roster generated yet."
              message={`Enter how many ${assetLabel.toLowerCase()}s the operator wants to onboard, then generate draft rows here.`}
            />
          ) : (
            <div className="grid gap-4">
              {draft.manualAssets.map((asset, index) => {
                const isReady = asset.assetNumber.trim() && asset.operatingUnitId.trim()

                return (
                  <div
                    key={asset.id}
                    className={cn(
                      "rounded-3xl border px-4 py-4",
                      isReady
                        ? "border-emerald-300/70 bg-emerald-50/60"
                        : "border-border/70 bg-background/70"
                    )}
                  >
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <div>
                        <div className="text-sm font-semibold">
                          {asset.assetNumber.trim() || `${assetLabel} ${index + 1}`}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {isReady
                            ? "Ready for review"
                            : `Needs ${assetLabel.toLowerCase()} number and operating-unit mapping`}
                        </div>
                      </div>
                      <div className="rounded-full border border-border/70 px-3 py-1 text-xs font-medium uppercase tracking-[0.16em] text-muted-foreground">
                        {assetLabel}
                      </div>
                    </div>

                    <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                      <label className="grid gap-2 text-sm">
                        <span className="text-muted-foreground">{assetLabel} number</span>
                        <input
                          className="rounded-2xl border border-border bg-background px-3 py-2"
                          value={asset.assetNumber}
                          onChange={(event) =>
                            updateManualAsset(asset.id, "assetNumber", event.target.value)
                          }
                          placeholder={
                            selectedVertical === "two_wheeler_delivery"
                              ? "KA01ZX1234"
                              : selectedVertical === "intercity_bus"
                                ? "KA57F1234"
                                : "KA01AB1234"
                          }
                        />
                      </label>

                      <label className="grid gap-2 text-sm">
                        <span className="text-muted-foreground">Operating unit</span>
                        <select
                          className="rounded-2xl border border-border bg-background px-3 py-2"
                          value={asset.operatingUnitId}
                          onChange={(event) =>
                            updateManualAsset(asset.id, "operatingUnitId", event.target.value)
                          }
                        >
                          <option value="">Select operating unit</option>
                          {completeOperatingUnits.map((unit) => (
                            <option key={unit.id} value={unit.id}>
                              {unit.code} • {unit.name}
                            </option>
                          ))}
                        </select>
                      </label>

                      <label className="grid gap-2 text-sm">
                        <span className="text-muted-foreground">{primaryOperatorLabel}</span>
                        <input
                          className="rounded-2xl border border-border bg-background px-3 py-2"
                          value={asset.primaryOperatorName}
                          onChange={(event) =>
                            updateManualAsset(asset.id, "primaryOperatorName", event.target.value)
                          }
                          placeholder={selectedVertical === "two_wheeler_delivery" ? "Assigned rider" : "Assigned primary operator"}
                        />
                      </label>

                      <label className="grid gap-2 text-sm">
                        <span className="text-muted-foreground">{primaryOperatorLabel} phone</span>
                        <input
                          className="rounded-2xl border border-border bg-background px-3 py-2"
                          value={asset.primaryOperatorPhone}
                          onChange={(event) =>
                            updateManualAsset(asset.id, "primaryOperatorPhone", event.target.value)
                          }
                          placeholder="+919900001111"
                        />
                      </label>

                      <label className="grid gap-2 text-sm">
                        <span className="text-muted-foreground">{secondaryOperatorLabel}</span>
                        <input
                          className="rounded-2xl border border-border bg-background px-3 py-2"
                          value={asset.secondaryOperatorName}
                          onChange={(event) =>
                            updateManualAsset(asset.id, "secondaryOperatorName", event.target.value)
                          }
                          placeholder={selectedVertical === "intercity_bus" ? "Conductor / attendant" : secondaryOperatorLabel}
                        />
                      </label>

                      <label className="grid gap-2 text-sm">
                        <span className="text-muted-foreground">{secondaryOperatorLabel} phone</span>
                        <input
                          className="rounded-2xl border border-border bg-background px-3 py-2"
                          value={asset.secondaryOperatorPhone}
                          onChange={(event) =>
                            updateManualAsset(asset.id, "secondaryOperatorPhone", event.target.value)
                          }
                          placeholder="+919900009999"
                        />
                      </label>

                      <label className="grid gap-2 text-sm">
                        <span className="text-muted-foreground">{routeLabel}</span>
                        <input
                          className="rounded-2xl border border-border bg-background px-3 py-2"
                          value={asset.routeOrZone}
                          onChange={(event) =>
                            updateManualAsset(asset.id, "routeOrZone", event.target.value)
                          }
                          placeholder={
                            selectedVertical === "intercity_bus"
                              ? "Bengaluru -> Hyderabad"
                              : selectedVertical === "two_wheeler_delivery"
                                ? "Indiranagar evening zone"
                                : "South corridor lane 4"
                          }
                        />
                      </label>

                      <label className="grid gap-2 text-sm">
                        <span className="text-muted-foreground">{notesLabel}</span>
                        <input
                          className="rounded-2xl border border-border bg-background px-3 py-2"
                          value={asset.notes}
                          onChange={(event) =>
                            updateManualAsset(asset.id, "notes", event.target.value)
                          }
                          placeholder="Optional structured notes for this asset"
                        />
                      </label>
                    </div>
                  </div>
                )
              })}

              <div className="flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={onContinueToReview}
                  disabled={!canContinueToReview}
                  className="rounded-2xl bg-primary px-4 py-2 text-sm font-medium text-primary-foreground disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Continue to review
                </button>
                {!canContinueToReview ? (
                  <OperatorStatusMessage tone="warning">
                    Every draft row needs an asset number and operating-unit mapping before the review step can continue.
                  </OperatorStatusMessage>
                ) : null}
              </div>
            </div>
          )}
        </OperatorPanel>
      </div>
    </FleetB2BPageShell>
  )
}
